
import { endOfDay, startOfDay, subDays } from 'date-fns'

import type {
  GetAnalyticsTotalsDataResponse,
  GetAnalyticsStatisticsDataResponse,
  GetAnalyticsScansMapDataResponse,
  AnalyticsScansMapCache,
  GetAnalyticsRoiDataResponse,
  AnalyticsFilters,
} from './types'
import type { Action, Nullable } from '../model'

import createReducer from '../createReducer'
import { Actions } from './types'




/**
 * The range the analytics page SHOULD open on: the company's current billing period.
 *
 * ── WHY THE BILLING PERIOD, AND NOT "THIS MONTH" OR "ALL TIME" ────────────────────
 *
 * The ROI hero divides value delivered by Vera spend, and that denominator is EXACT ONLY
 * INSIDE THE CURRENT BILLING PERIOD. `company_subscriptions` holds one row per company —
 * current state, no plan history — so any range reaching outside the current period can
 * only assume today's plan and price applied throughout. The API says so itself: it returns
 * `exact: false` plus an `approximation_note`, and the page renders that as the amber
 * caveat under the hero (`getVeraSpend`, `analytics.postgres.repository.ts`; the flag is
 * `from >= current_period_start && to <= current_period_end`).
 *
 * So the choice of default decides whether the page's HEADLINE NUMBER is exact or
 * approximate on arrival. "This month" and "All time" both straddle the period boundary for
 * most brands most of the time, which ships the caveat permanently — a disclaimer nobody
 * asked for, attached to the first figure anyone reads. Defaulting to the billing period
 * makes the figure exact on first load, and the caveat then appears only when someone
 * deliberately widens the range, which is exactly when it is informative.
 *
 * ── WHY THIS IS A FUNCTION AND NOT THE `defaultFilters` VALUE BELOW ───────────────
 *
 * The boundaries are per-company data. This module is evaluated once when the store is
 * created, long before any company is known, so the default filters CANNOT be the billing
 * period — the store would have to invent it. Approximating with `startOfMonth(now)` was
 * considered and rejected: it would silently reintroduce the very approximation this
 * default exists to remove, while looking like it had fixed it.
 *
 * The billing period therefore has to be applied once the boundaries resolve —
 * `dispatch(setAnalyticsFilters({ dateRange: billingPeriodDateRange(spend) }))`, which is
 * what `AnalyticsFiltersHeader` does exactly once — and `BOOTSTRAP_DATE_RANGE` below stays
 * the day-boundaried 30-day window used until then.
 *
 * ── WHERE THE BOUNDARIES COME FROM ────────────────────────────────────────────────
 *
 * The ROI response's `roi.spend.current_period_start` / `current_period_end`
 * (`IVeraSpend`), NOT the company-subscription endpoint. Both read the same
 * `company_subscriptions` row, but only the ROI response's copy is the one `getVeraSpend`
 * COMPARED AGAINST when it set `exact` — it returns the very values it tested. A range
 * built from them therefore satisfies `from >= current_period_start && to <=
 * current_period_end` by construction. Built from a second endpoint's copy it would only
 * satisfy it as long as the two agree, and the failure when they do not is silent: the
 * page shows the approximation caveat under a range that was chosen specifically to
 * remove it.
 *
 * Returns `null` when the boundaries are unknown (no subscription, trial, never
 * provisioned), so the caller keeps the bootstrap window rather than guessing.
 */
export const billingPeriodDateRange = (period?: {
  current_period_start?: string | Date | null
  current_period_end?: string | Date | null
} | null): { from: Date; to: Date } | null => {
  if (!period?.current_period_start || !period?.current_period_end) return null

  const from = new Date(period.current_period_start)
  const end = new Date(period.current_period_end)
  if (Number.isNaN(from.getTime()) || Number.isNaN(end.getTime())) return null

  // NOT day-floored. `getVeraSpend` tests `from >= current_period_start`, so rounding down
  // to midnight would put `from` before the period start and hand back the approximation.
  //
  // The end is clamped to the end of today because the period END is the NEXT RENEWAL —
  // a future instant. Unclamped it asks for scans that have not happened, and the picker
  // (`maxDateTime={dayjs().endOf('day')}`) renders the field in its error state.
  const today = endOfDay(new Date())
  return { from, to: end < today ? end : today }
}

/**
 * The BOOTSTRAP window — what the page shows until the billing period can be applied.
 * See `billingPeriodDateRange` above for why the intended default cannot be expressed
 * at store-creation time.
 *
 * DAY-BOUNDARIED, and the SAME LENGTH as before.
 *
 * This was `[now - 30d, now]` — a rolling window ending at the current wall-clock instant.
 * Two problems, one visible and one not:
 *
 *  - VISIBLE: the analytics date field renders a clock time whenever the range is not
 *    day-boundaried (see `isWholeDayRange`), and an arbitrary `2:41 PM` on both ends of the
 *    DEFAULT range is precisely the ~44-character form the compact one exists to avoid.
 *    The time was never chosen by anyone; it was whatever `new Date()` happened to be.
 *  - QUIET: a window ending mid-afternoon reports "the last 30 days" while excluding
 *    everything that happens for the rest of today, and includes a part-day at the far end
 *    to make up for it. Nobody notices a ragged edge; they just see a slightly wrong total.
 *
 * `subDays(…, 29)` — not 30 — is deliberate: 29 days back at 00:00 through today at 23:59
 * is 30 CALENDAR days, so the window's LENGTH is unchanged (720h, modulo DST). That
 * matters because the ROI hero prorates Vera spend by range length, so a default that
 * silently grew to 31 days would have moved the headline multiple on every brand's page
 * without anything on it changing.
 *
 * ── WHY THIS IS EXPORTED, AND WHY ITS IDENTITY IS LOAD-BEARING ────────────────────
 *
 * It is the "nobody has chosen a range yet" sentinel. The billing-period default is
 * applied ONCE, after the subscription boundaries arrive, and it must never overwrite a
 * range the user has since picked — so the applier needs to distinguish "still the
 * bootstrap" from "someone set this".
 *
 * REFERENCE equality answers that exactly, and a value comparison would not. This module
 * is evaluated once, so this object is a stable identity for the store's lifetime;
 * `SET_ANALYTICS_FILTERS` spreads a fresh object only when `dateRange` is in the payload,
 * so `state.filters.dateRange === BOOTSTRAP_DATE_RANGE` is true if and only if no writer
 * has touched it. (A value comparison would also report "untouched" for a user who
 * deliberately re-picked this very window — a rare case, but one where silently replacing
 * their choice is the wrong answer.) There are exactly two writers today:
 * `AnalyticsFiltersHeader`'s picker and `ScansByTime2`'s "Apply to page"; both dispatch a
 * new object, so both are detected.
 */
export const BOOTSTRAP_DATE_RANGE: { from: Date; to: Date } = {
  from: startOfDay(subDays(new Date(), 29)),
  to: endOfDay(new Date()),
}

/**
 * How many distinct scans-map requests stay cached. See `SET_ANALYTICS_SCANS_MAP`.
 *
 * EIGHT: one entry per live map card, plus the entry each is leaving behind mid-filter-
 * change. It is derived from the mappable-dimension count rather than picked — there are
 * THREE mappable dimensions since VERA-260 (`scans`, `certificates`, `invalid_scans`; see
 * `MAP_RENDERABLE`), so three cards can be on the canvas at once and 3 x 2 = 6 is the
 * working set, with two spare.
 *
 * ⚠️ IT WAS SIX, AND SIX WAS ALREADY EXACTLY THE WORKING SET when there were two
 * dimensions. Eviction is oldest-first over insertion order, so a limit equal to the
 * working set evicts a LIVE card's entry the moment anything transient appears — and the
 * card that lost its entry reads `null`, which `ScansByLocation` cannot distinguish from
 * "in flight". Leaving headroom is what keeps that unreachable rather than merely unlikely.
 *
 * A dimension added to `MAP_RENDERABLE` should raise this by two.
 */
const SCANS_MAP_CACHE_LIMIT = 8

export interface AnalyticsReducer {
  totals: Nullable<GetAnalyticsTotalsDataResponse>
  statistics: Nullable<GetAnalyticsStatisticsDataResponse>
  /**
   * KEYED BY REQUEST, unlike every other slot here — see `AnalyticsScansMapCache` in
   * `./types` for why the page can hold several map cards asking different questions and
   * what a single slot did to them.
   */
  scansMap: AnalyticsScansMapCache
  roi: Nullable<GetAnalyticsRoiDataResponse>
  /**
   * The HERO's ROI response, measured over the hero's OWN period rather than the page's
   * date range. Separate from `roi` because the two are different windows and this slot
   * is overwritten wholesale — see `Actions.GET_ANALYTICS_ROI_HERO`.
   *
   * ⚠️ `AnalyticsFiltersHeader` reads the billing-period boundaries from `roi`, NOT from
   * here, and must keep doing so: `roi` is the request the page's own picker bootstraps
   * from, and reading the boundaries out of a slot the hero can re-request would couple
   * the page's default range to the hero's control. Both slots carry the same
   * `spend.current_period_start` / `current_period_end` (they come from the same
   * `company_subscriptions` row), so this is a choice about coupling, not about accuracy.
   */
  roiHero: Nullable<GetAnalyticsRoiDataResponse>
  filters: AnalyticsFilters
}

const defaultFilters: AnalyticsFilters = {
  ownerIds: [],
  productIds: [],
  certificateIds: [],
  certificateStatuses: [],
  /** Scan outcomes (VERA-260) — empty means no narrowing. See `AnalyticsFilters`. */
  scanStatuses: [],
  /** See `BOOTSTRAP_DATE_RANGE` — the identity of this object is the "untouched" sentinel. */
  dateRange: BOOTSTRAP_DATE_RANGE,
  location: [],
  /** No drawn area. See `AnalyticsFilters.area` for why `null` and not `[]`. */
  area: null,
  // Per-panel, and both default to scans — see `AnalyticsFilters` for why this is two
  // keys rather than one.
  chartDataType: 'scans',
  mapDataType: 'scans',
}

const defaultState: AnalyticsReducer = {
  totals: null,
  statistics: null,
  scansMap: {},
  roi: null,
  roiHero: null,
  filters: defaultFilters,
}

export const analyticsReducer = createReducer<AnalyticsReducer>(defaultState, {
  [Actions.SET_ANALYTICS_TOTALS]: (
    state: Nullable<AnalyticsReducer>,
    action: Action<Nullable<GetAnalyticsTotalsDataResponse>>
  ) => ({
    ...state,
    totals: action.payload,
  }),

  [Actions.SET_ANALYTICS_STATISTICS]: (
    state: Nullable<AnalyticsReducer>,
    action: Action<Nullable<GetAnalyticsStatisticsDataResponse>>
  ) => ({
    ...state,
    statistics: action.payload,
  }),

  /**
   * Merge by key, and keep the cache BOUNDED.
   *
   * `takeEvery` means every distinct request now lands rather than being cancelled, so an
   * unbounded map would accumulate one entry per filter change for the life of the
   * session — and each entry is a full point list (336 in local seed data, far more for a
   * real brand). The bound is small on purpose: the live consumers are the legacy map card
   * plus whatever map variants are on the canvas, so anything past the most recent handful
   * is by definition nobody's current key.
   *
   * Insertion order is the eviction order, which is right here rather than merely
   * convenient: re-writing an existing key does NOT move it (object key order is
   * insertion-ordered and a re-assignment keeps the original position), so a card whose
   * data refreshes on a timer cannot walk a sibling's entry out of the cache.
   */
  [Actions.SET_ANALYTICS_SCANS_MAP]: (
    state: Nullable<AnalyticsReducer>,
    action: Action<{ key: string; response: GetAnalyticsScansMapDataResponse }>
  ) => {
    const next: AnalyticsScansMapCache = {
      ...(state?.scansMap ?? {}),
      [action.payload.key]: action.payload.response,
    }
    const keys = Object.keys(next)
    for (const stale of keys.slice(0, Math.max(0, keys.length - SCANS_MAP_CACHE_LIMIT))) {
      delete next[stale]
    }
    return { ...state, scansMap: next }
  },

  [Actions.SET_ANALYTICS_ROI]: (
    state: Nullable<AnalyticsReducer>,
    action: Action<Nullable<GetAnalyticsRoiDataResponse>>
  ) => ({
    ...state,
    roi: action.payload,
  }),

  [Actions.SET_ANALYTICS_ROI_HERO]: (
    state: Nullable<AnalyticsReducer>,
    action: Action<Nullable<GetAnalyticsRoiDataResponse>>
  ) => ({
    ...state,
    roiHero: action.payload,
  }),

  [Actions.SET_ANALYTICS_FILTERS]: (
    state: AnalyticsReducer,
    action: Action<Partial<AnalyticsFilters>>
  ) => ({
    ...state,
    filters: {
      ...state.filters,
      ...action.payload,
    },
  }),
})
